(() => {
  const ROOT_SELECTOR = '[data-sdw-visual-search]';
  const OCR_SOURCE = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
  const MAX_FILE_SIZE = 15 * 1024 * 1024;

  const STOP_WORDS = new Set([
    'THE','AND','WITH','FROM','THIS','THAT','FOR','OF','TO','IN','ON','A','AN','IS','ARE','BY','OR','AS','AT',
    'WARNING','KEEP','OUT','REACH','CHILDREN','ONLY','MADE','PRODUCT','PACKAGING','PLEASE','READ','BEFORE',
    'USE','DO','NOT','DISPOSE','RESPONSIBLY','WWW','COM','NET','CO','UK','CE','ML','MG','NICOTINE','CONTAINS'
  ]);

  function loadOcrLibrary() {
    if (window.Tesseract) return Promise.resolve(window.Tesseract);
    if (window.__sdwVisualOcrPromise) return window.__sdwVisualOcrPromise;

    window.__sdwVisualOcrPromise = new Promise((resolve, reject) => {
      const current = document.querySelector(`script[src="${OCR_SOURCE}"]`);
      if (current) {
        current.addEventListener('load', () => resolve(window.Tesseract), { once: true });
        current.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = OCR_SOURCE;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => window.Tesseract ? resolve(window.Tesseract) : reject(new Error('OCR library unavailable'));
      script.onerror = () => reject(new Error('Could not load OCR library'));
      document.head.appendChild(script);
    });

    return window.__sdwVisualOcrPromise;
  }

  function normalise(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/&/g, ' AND ')
      .replace(/[^A-Z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function unique(values) {
    return [...new Set(values.filter(Boolean))];
  }

  function cleanTokens(rawText) {
    return unique(
      normalise(rawText)
        .split(' ')
        .filter((token) => token.length >= 2 && !STOP_WORDS.has(token))
        .filter((token) => /[A-Z]/.test(token) || /\d/.test(token))
    ).slice(0, 16);
  }

  function isModelToken(token) {
    return (/[A-Z]/.test(token) && /\d/.test(token)) || /^\d{6,}$/.test(token);
  }

  function buildQueries(tokens, barcodes = []) {
    const models = tokens.filter(isModelToken);
    const words = tokens.filter((token) => !isModelToken(token));
    const longest = [...words].sort((a, b) => b.length - a.length);
    const candidates = [
      ...barcodes,
      tokens.slice(0, 7).join(' '),
      tokens.slice(0, 5).join(' '),
      [words[0], ...models.slice(0, 2)].filter(Boolean).join(' '),
      models.slice(0, 2).join(' '),
      [longest[0], longest[1]].filter(Boolean).join(' '),
      longest[0] || ''
    ];

    return unique(candidates.map((value) => String(value || '').trim())).slice(0, 7);
  }

  function getResourceImage(resource) {
    if (!resource) return '';
    if (typeof resource.image === 'string') return resource.image;
    if (resource.image?.url) return resource.image.url;
    if (typeof resource.featured_image === 'string') return resource.featured_image;
    if (resource.featured_image?.url) return resource.featured_image.url;
    if (typeof resource.featuredImage === 'string') return resource.featuredImage;
    if (resource.featuredImage?.url) return resource.featuredImage.url;
    return '';
  }

  function getProductPrice(product) {
    return product.price || product.price_min || product.price_max || '';
  }

  function isProductAvailable(product) {
    return product?.available !== false;
  }

  async function detectBarcodes(file) {
    if (!('BarcodeDetector' in window) || !('createImageBitmap' in window)) return [];

    try {
      const supported = await window.BarcodeDetector.getSupportedFormats();
      const preferred = ['ean_13','ean_8','upc_a','upc_e','code_128','qr_code'].filter((format) => supported.includes(format));
      if (!preferred.length) return [];
      const bitmap = await createImageBitmap(file);
      const detector = new window.BarcodeDetector({ formats: preferred });
      const results = await detector.detect(bitmap);
      bitmap.close?.();
      return unique(results.map((entry) => normalise(entry.rawValue).replace(/\s/g, '')).filter(Boolean));
    } catch (error) {
      console.info('[Sweet Deals visual search] Barcode detection unavailable.', error);
      return [];
    }
  }

  async function searchCatalogue(root, query) {
    const endpoint = root.dataset.searchEndpoint || '/search/suggest';
    const limit = Number.parseInt(root.dataset.resultsLimit || '8', 10);
    const endpointPath = endpoint.endsWith('.json') ? endpoint : `${endpoint}.json`;
    const url = new URL(endpointPath, window.location.origin);

    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', String(Math.max(10, limit)));
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`Search failed (${response.status})`);
    const payload = await response.json();
    const results = payload?.resources?.results || {};

    return {
      query,
      products: Array.isArray(results.products) ? results.products : [],
      collections: Array.isArray(results.collections) ? results.collections : []
    };
  }

  function mergeSearchResults(resultSets) {
    const productMap = new Map();
    const collectionMap = new Map();

    resultSets.forEach((set) => {
      set.products.forEach((product) => {
        const key = product.url || product.id || normalise(product.title);
        const existing = productMap.get(key);
        if (existing) {
          existing._queries = unique([...(existing._queries || []), set.query]);
        } else {
          productMap.set(key, { ...product, _queries: [set.query] });
        }
      });

      set.collections.forEach((collection) => {
        const key = collection.url || collection.id || normalise(collection.title);
        const existing = collectionMap.get(key);
        if (existing) {
          existing._queries = unique([...(existing._queries || []), set.query]);
        } else {
          collectionMap.set(key, { ...collection, _queries: [set.query] });
        }
      });
    });

    return {
      products: [...productMap.values()],
      collections: [...collectionMap.values()]
    };
  }

  function scoreProduct(product, evidenceTokens, queries, barcodes) {
    const title = normalise(product.title);
    const searchable = normalise([product.title, product.vendor, product.type].filter(Boolean).join(' '));
    const relevantTokens = evidenceTokens.filter((token) => token.length >= 3 || isModelToken(token));
    const matchedTokens = relevantTokens.filter((token) => searchable.includes(token));
    const models = relevantTokens.filter(isModelToken);
    const matchedModels = models.filter((token) => searchable.includes(token));
    const coverage = relevantTokens.length ? matchedTokens.length / relevantTokens.length : 0;
    const modelCoverage = models.length ? matchedModels.length / models.length : 0;
    const phraseHit = queries.some((query) => {
      const phrase = normalise(query);
      return phrase.split(' ').length >= 2 && title.includes(phrase);
    });
    const barcodeHit = barcodes.some((barcode) => (product._queries || []).some((query) => normalise(query).replace(/\s/g, '') === barcode));
    const firstWordHit = relevantTokens[0] ? title.includes(relevantTokens[0]) : false;
    const score = Math.min(1.2, coverage * 0.55 + modelCoverage * 0.24 + (phraseHit ? 0.16 : 0) + (firstWordHit ? 0.05 : 0) + (barcodeHit ? 0.45 : 0));

    const exact = isProductAvailable(product) && (
      barcodeHit ||
      (phraseHit && matchedTokens.length >= 2 && coverage >= 0.55) ||
      (models.length > 0 && matchedModels.length === models.length && matchedTokens.length >= 2 && score >= 0.68) ||
      (score >= 0.82 && matchedTokens.length >= 3)
    );

    return { score, exact, matchedTokens: matchedTokens.length, barcodeHit, phraseHit };
  }

  function scoreCollection(collection, evidenceTokens) {
    const title = normalise(collection.title);
    const matched = evidenceTokens.filter((token) => token.length >= 3 && title.includes(token)).length;
    return matched + ((collection._queries || []).length * 0.08);
  }

  function createMedia(resource, className) {
    const media = document.createElement('div');
    media.className = className;
    const source = getResourceImage(resource);

    if (source) {
      const image = document.createElement('img');
      image.src = source;
      image.alt = resource.title || 'Product image';
      image.loading = 'lazy';
      media.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'sdw-visual-card__placeholder';
      placeholder.textContent = 'Image unavailable';
      media.appendChild(placeholder);
    }

    return media;
  }

  function createExactCard(product, score) {
    const card = document.createElement('a');
    card.className = 'sdw-visual-exact-card';
    card.href = product.url || '#';

    const media = createMedia(product, 'sdw-visual-exact-card__media');
    const body = document.createElement('div');
    body.className = 'sdw-visual-exact-card__body';

    const vendor = document.createElement('p');
    vendor.className = 'sdw-visual-card__vendor';
    vendor.textContent = product.vendor || product.type || 'Catalogue product';

    const title = document.createElement('h4');
    title.textContent = product.title || 'Product';

    const reason = document.createElement('p');
    reason.className = 'sdw-visual-exact-card__match';
    reason.textContent = score.barcodeHit ? 'Matched using a detected product barcode.' : 'Matched using the strongest combination of product name and model evidence.';

    const bottom = document.createElement('div');
    bottom.className = 'sdw-visual-exact-card__bottom';
    const price = document.createElement('span');
    price.className = 'sdw-visual-card__price';
    price.textContent = getProductPrice(product);
    const cta = document.createElement('span');
    cta.className = 'sdw-visual-card__cta';
    cta.textContent = 'View product →';
    bottom.append(price, cta);

    body.append(vendor, title, reason, bottom);
    card.append(media, body);
    return card;
  }

  function createProductCard(product) {
    const card = document.createElement('a');
    card.className = 'sdw-visual-product-card';
    card.href = product.url || '#';

    const media = createMedia(product, 'sdw-visual-product-card__media');
    const body = document.createElement('div');
    body.className = 'sdw-visual-product-card__body';

    const vendor = document.createElement('p');
    vendor.className = 'sdw-visual-card__vendor';
    vendor.textContent = product.vendor || product.type || '';

    const title = document.createElement('h4');
    title.textContent = product.title || 'Product';

    const bottom = document.createElement('div');
    bottom.className = 'sdw-visual-product-card__bottom';
    const price = document.createElement('span');
    price.className = 'sdw-visual-card__price';
    price.textContent = getProductPrice(product);
    const cta = document.createElement('span');
    cta.className = 'sdw-visual-card__cta';
    cta.textContent = 'View →';
    bottom.append(price, cta);

    body.append(vendor, title, bottom);
    card.append(media, body);
    return card;
  }

  function createCollectionCard(collection) {
    const card = document.createElement('a');
    card.className = 'sdw-visual-collection-card';
    card.href = collection.url || '#';

    const media = document.createElement('div');
    media.className = 'sdw-visual-collection-card__media';
    const source = getResourceImage(collection);
    if (source) {
      const image = document.createElement('img');
      image.src = source;
      image.alt = collection.title || 'Collection image';
      image.loading = 'lazy';
      media.appendChild(image);
    } else {
      const symbol = document.createElement('span');
      symbol.textContent = '↗';
      media.appendChild(symbol);
    }

    const body = document.createElement('div');
    body.className = 'sdw-visual-collection-card__body';
    const label = document.createElement('small');
    label.textContent = 'Collection';
    const title = document.createElement('strong');
    title.textContent = collection.title || 'Collection';
    body.append(label, title);
    card.append(media, body);
    return card;
  }

  function initialise(root) {
    if (!root || root.dataset.sdwVisualSearchReady === 'true') return;
    root.dataset.sdwVisualSearchReady = 'true';

    const modal = root.querySelector('[data-sdw-image-modal]');
    const dialog = root.querySelector('[data-sdw-image-dialog]');
    const openers = [...root.querySelectorAll('[data-sdw-image-open]')];
    const closers = [...root.querySelectorAll('[data-sdw-image-close]')];
    const fileInput = root.querySelector('[data-sdw-image-file]');
    const cameraInput = root.querySelector('[data-sdw-image-camera]');
    const dropzone = root.querySelector('[data-sdw-image-dropzone]');
    const preview = root.querySelector('[data-sdw-image-preview]');
    const placeholder = root.querySelector('[data-sdw-image-placeholder]');
    const status = root.querySelector('[data-sdw-image-status]');
    const progress = root.querySelector('[data-sdw-image-progress]');
    const progressBar = root.querySelector('[data-sdw-image-progress-bar]');
    const queryInput = root.querySelector('[data-sdw-image-query]');
    const refineForm = root.querySelector('[data-sdw-image-refine]');
    const emptyState = root.querySelector('[data-sdw-image-empty]');
    const exactSection = root.querySelector('[data-sdw-exact-section]');
    const exactGrid = root.querySelector('[data-sdw-exact-grid]');
    const collectionsSection = root.querySelector('[data-sdw-collections-section]');
    const collectionsGrid = root.querySelector('[data-sdw-collections-grid]');
    const similarSection = root.querySelector('[data-sdw-similar-section]');
    const similarGrid = root.querySelector('[data-sdw-similar-grid]');
    const resultsFooter = root.querySelector('[data-sdw-results-footer]');
    const fullSearch = root.querySelector('[data-sdw-full-search]');
    const searchBarInputs = [...root.querySelectorAll('[data-sdw-search-input]')];

    if (!modal || !dialog) return;

    let lastOpener = null;
    let previewUrl = '';
    let requestSequence = 0;

    const setStatus = (message, state = '') => {
      status.textContent = message;
      status.classList.toggle('is-error', state === 'error');
      status.classList.toggle('is-success', state === 'success');
    };

    const setBusy = (busy) => {
      progress.hidden = !busy;
      root.classList.toggle('is-visual-searching', busy);
      if (!busy) progressBar.style.width = '0%';
    };

    const resetResults = () => {
      emptyState.hidden = false;
      exactSection.hidden = true;
      collectionsSection.hidden = true;
      similarSection.hidden = true;
      resultsFooter.hidden = true;
      exactGrid.replaceChildren();
      collectionsGrid.replaceChildren();
      similarGrid.replaceChildren();
    };

    const openModal = (opener) => {
      lastOpener = opener || document.activeElement;
      modal.hidden = false;
      document.body.classList.add('sdw-visual-search-open');
      window.requestAnimationFrame(() => dialog.focus());
    };

    const closeModal = () => {
      modal.hidden = true;
      document.body.classList.remove('sdw-visual-search-open');
      lastOpener?.focus?.();
    };

    const syncDetectedQuery = (query) => {
      queryInput.value = query;
      searchBarInputs.forEach((input) => { input.value = query; });
    };

    const showPreview = (file) => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = URL.createObjectURL(file);
      preview.src = previewUrl;
      preview.hidden = false;
      placeholder.hidden = true;
    };

    const renderMatches = ({ products, collections }, evidenceTokens, queries, barcodes, preferredQuery) => {
      const limit = Number.parseInt(root.dataset.resultsLimit || '8', 10);
      const scoredProducts = products
        .map((product) => ({ product, match: scoreProduct(product, evidenceTokens, queries, barcodes) }))
        .sort((a, b) => Number(b.match.exact) - Number(a.match.exact) || b.match.score - a.match.score || Number(isProductAvailable(b.product)) - Number(isProductAvailable(a.product)));

      const exactCandidate = scoredProducts.find((entry) => entry.match.exact);
      const unavailableStrongCandidate = scoredProducts.find((entry) => !isProductAvailable(entry.product) && entry.match.score >= 0.72);
      const rankedCollections = collections
        .map((collection) => ({ collection, score: scoreCollection(collection, evidenceTokens) }))
        .sort((a, b) => b.score - a.score)
        .map((entry) => entry.collection)
        .slice(0, 12);

      emptyState.hidden = true;
      exactSection.hidden = !exactCandidate;
      collectionsSection.hidden = rankedCollections.length === 0;
      similarSection.hidden = Boolean(exactCandidate);
      resultsFooter.hidden = false;

      if (exactCandidate) {
        exactGrid.replaceChildren(createExactCard(exactCandidate.product, exactCandidate.match));
      } else {
        exactGrid.replaceChildren();
        const similar = scoredProducts
          .filter((entry) => isProductAvailable(entry.product))
          .slice(0, limit)
          .map((entry) => entry.product);
        similarGrid.replaceChildren(...similar.map(createProductCard));
        similarSection.hidden = similar.length === 0;
      }

      collectionsGrid.replaceChildren(...rankedCollections.map(createCollectionCard));
      const searchUrl = root.dataset.searchUrl || '/search';
      fullSearch.href = `${searchUrl}?q=${encodeURIComponent(preferredQuery)}&type=product`;

      if (exactCandidate) {
        setStatus(`Exact available product found for “${preferredQuery}”.`, 'success');
      } else if (unavailableStrongCandidate) {
        setStatus('The closest exact item appears unavailable, so available similar products are shown.', 'success');
      } else if (scoredProducts.length || rankedCollections.length) {
        setStatus('No verified exact product was found. Similar products and matching collections are shown.', 'success');
      } else {
        emptyState.hidden = false;
        emptyState.querySelector('strong').textContent = 'No catalogue matches found';
        emptyState.querySelector('p').textContent = 'Try a clearer front-facing photo or edit the detected product words.';
        resultsFooter.hidden = false;
        setStatus('No product or collection matches were found.', 'error');
      }
    };

    const runCatalogueSearch = async (queries, evidenceTokens, barcodes) => {
      const sequence = ++requestSequence;
      const settled = await Promise.allSettled(queries.slice(0, 6).map((query) => searchCatalogue(root, query)));
      if (sequence !== requestSequence) return;
      const successful = settled.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      if (!successful.length) throw new Error('No catalogue search completed');
      const merged = mergeSearchResults(successful);
      renderMatches(merged, evidenceTokens, queries, barcodes, queries[0]);
    };

    const analyseFile = async (file) => {
      if (!file || !String(file.type).startsWith('image/')) {
        setStatus('Please choose a valid image file.', 'error');
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setStatus('The image is too large. Choose an image under 15 MB.', 'error');
        return;
      }

      resetResults();
      showPreview(file);
      setBusy(true);
      setStatus('Checking the photo for a barcode and product text…');

      try {
        const barcodePromise = detectBarcodes(file);
        const Tesseract = await loadOcrLibrary();
        const recognition = await Tesseract.recognize(file, 'eng', {
          logger: (event) => {
            if (typeof event.progress === 'number') {
              progressBar.style.width = `${Math.max(5, Math.round(event.progress * 100))}%`;
            }
            if (event.status) {
              const readable = event.status.charAt(0).toUpperCase() + event.status.slice(1);
              setStatus(`${readable}…`);
            }
          }
        });

        const barcodes = await barcodePromise;
        const evidenceTokens = cleanTokens(recognition?.data?.text || '');
        const queries = buildQueries(evidenceTokens, barcodes);

        if (!queries.length) {
          setStatus('No useful product text or barcode was detected. Enter the product name manually.', 'error');
          queryInput.focus();
          return;
        }

        syncDetectedQuery(queries[0]);
        setStatus(`Detected “${queries[0]}”. Ranking products and collections…`);
        await runCatalogueSearch(queries, evidenceTokens, barcodes);
      } catch (error) {
        console.error('[Sweet Deals visual search]', error);
        setStatus('Image recognition could not finish. Enter the visible product name and search again.', 'error');
        queryInput.focus();
      } finally {
        setBusy(false);
      }
    };

    const searchRefinedText = async (rawQuery) => {
      const query = String(rawQuery || '').trim();
      if (!query) {
        setStatus('Enter a product name or model number.', 'error');
        return;
      }

      const evidenceTokens = cleanTokens(query);
      const queries = buildQueries(evidenceTokens);
      syncDetectedQuery(query);
      resetResults();
      setBusy(true);
      setStatus(`Searching for “${query}”…`);
      try {
        await runCatalogueSearch(queries.length ? queries : [query], evidenceTokens, []);
      } catch (error) {
        console.error('[Sweet Deals visual search]', error);
        setStatus('The catalogue search could not be completed.', 'error');
      } finally {
        setBusy(false);
      }
    };

    openers.forEach((opener) => opener.addEventListener('click', () => openModal(opener)));
    closers.forEach((closer) => closer.addEventListener('click', closeModal));

    [fileInput, cameraInput].forEach((input) => {
      input?.addEventListener('click', () => { input.value = ''; });
      input?.addEventListener('change', () => analyseFile(input.files?.[0]));
    });

    dropzone?.addEventListener('dragenter', (event) => {
      event.preventDefault();
      dropzone.classList.add('is-dragging');
    });
    dropzone?.addEventListener('dragover', (event) => event.preventDefault());
    dropzone?.addEventListener('dragleave', () => dropzone.classList.remove('is-dragging'));
    dropzone?.addEventListener('drop', (event) => {
      event.preventDefault();
      dropzone.classList.remove('is-dragging');
      analyseFile(event.dataTransfer?.files?.[0]);
    });

    refineForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      searchRefinedText(queryInput.value);
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal();
      if (event.key === 'Tab') {
        const focusable = [...dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),label')].filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    window.addEventListener('pagehide', () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, { once: true });
  }

  function initialiseAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initialise(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
